import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Car, Clock, CheckCircle2, Sparkles, ChevronRight, ArrowLeft, Plus } from 'lucide-react';
import { tierServices, addOnServices, pricingMatrix } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const YEARS = Array.from({ length: 2027 - 1950 + 1 }, (_, i) => (2027 - i).toString());

const VEHICLE_TYPES = [
  'Sedan',
  'SUV (2 Row)',
  'SUV (3 Row)',
  'Coupe',
  'Truck',
  'Van',
  'RV',
  'Motorcycle',
  'Other'
];

const TIME_SLOTS = [
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' }
];

const BookingPage = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState('info');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleType: '',
    customVehicleType: '',
    preferredSlots: [
      { date: '', time: '' },
      { date: '', time: '' }
    ],
    // Tier selections: { 'Interior Detail': 'basic'|'moderate'|'mad', 'Exterior Detail': ... }
    tierSelections: {},
    selectedAddOns: [],
    otherService: '',
    additionalNotes: ''
  });

  const sections = [
    { id: 'info', label: 'Personal Info', icon: User },
    { id: 'vehicle', label: 'Vehicle Details', icon: Car },
    { id: 'services', label: 'Services', icon: Sparkles },
    { id: 'scheduling', label: 'Schedule', icon: Calendar },
    { id: 'summary', label: 'Summary', icon: CheckCircle2 }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'vehicleType' && value !== 'Other') {
        updated.customVehicleType = '';
      }
      return updated;
    });
  };

  // Availability per date - { 'YYYY-MM-DD': { hard: [...], soft: [...] } }
  const [availability, setAvailability] = useState({});
  const [loadingDates, setLoadingDates] = useState({});

  const fetchAvailability = async (date) => {
    if (!date || availability[date]) return;
    setLoadingDates(prev => ({ ...prev, [date]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/availability?date=${date}`);
      if (res.ok) {
        const data = await res.json();
        setAvailability(prev => ({ ...prev, [date]: { hard: data.hard || [], soft: data.soft || [] } }));
      }
    } catch (err) {
      // Silent fail - don't block user
    } finally {
      setLoadingDates(prev => ({ ...prev, [date]: false }));
    }
  };

  const handleSlotChange = (index, field, value) => {
    setFormData(prev => {
      const newSlots = [...prev.preferredSlots];
      newSlots[index] = { ...newSlots[index], [field]: value };
      // Clear time if date changed
      if (field === 'date') {
        newSlots[index].time = '';
      }
      return { ...prev, preferredSlots: newSlots };
    });
    if (field === 'date' && value) {
      fetchAvailability(value);
    }
  };

  const handleTierSelect = (category, tier) => {
    setFormData(prev => {
      const newTiers = { ...prev.tierSelections };
      if (newTiers[category] === tier) {
        // Deselect if clicking the same tier
        delete newTiers[category];
      } else {
        newTiers[category] = tier;
      }
      return { ...prev, tierSelections: newTiers };
    });
  };

  const handleAddOnToggle = (serviceName) => {
    setFormData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.includes(serviceName)
        ? prev.selectedAddOns.filter(s => s !== serviceName)
        : [...prev.selectedAddOns, serviceName]
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const slots = formData.preferredSlots.filter(s => s.date && s.time);
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        vehicleYear: formData.vehicleYear,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        vehicleType: formData.vehicleType,
        customVehicleType: formData.customVehicleType,
        preferredSlots: slots,
        tierSelections: formData.tierSelections,
        selectedAddOns: formData.selectedAddOns,
        otherService: formData.otherService,
        estimatedTotal: formatTotal() || ''
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit booking. Please try again.');
      }

      setSubmitted(true);
      setTimeout(() => {
        navigate('/');
      }, 4000);
    } catch (e) {
      setSubmitError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getFilledSlots = () => {
    return formData.preferredSlots.filter(slot => slot.date && slot.time);
  };

  const getSelectedServicesCount = () => {
    return Object.keys(formData.tierSelections).length + formData.selectedAddOns.length + (formData.otherService.trim() ? 1 : 0);
  };

  // Parse a price string into { low, high, openEnded } numbers
  const parsePrice = (priceStr) => {
    if (!priceStr || typeof priceStr !== 'string') return null;
    if (priceStr.toLowerCase().includes('custom') || priceStr.toLowerCase().includes('quote')) return null;

    // Handle "$1,500+ Starting"
    if (priceStr.includes('+')) {
      const match = priceStr.match(/\$?([\d,]+)/);
      if (match) {
        const val = parseInt(match[1].replace(/,/g, ''), 10);
        return { low: val, high: val, openEnded: true };
      }
      return null;
    }

    // Handle range "$125 – $175" or "$125 - $175"
    const rangeMatch = priceStr.match(/\$?([\d,]+)\s*[–-]\s*\$?([\d,]+)/);
    if (rangeMatch) {
      return {
        low: parseInt(rangeMatch[1].replace(/,/g, ''), 10),
        high: parseInt(rangeMatch[2].replace(/,/g, ''), 10),
        openEnded: false
      };
    }

    // Handle single price "$125"
    const singleMatch = priceStr.match(/\$?([\d,]+)/);
    if (singleMatch) {
      const val = parseInt(singleMatch[1].replace(/,/g, ''), 10);
      return { low: val, high: val, openEnded: false };
    }

    return null;
  };

  // Calculate total price range for selected tier services
  const calculateTotal = () => {
    let lowTotal = 0;
    let highTotal = 0;
    let hasOpenEnded = false;
    let hasItems = false;

    Object.entries(formData.tierSelections).forEach(([category, tier]) => {
      const categoryKey = category === 'Interior Detail' ? 'interior' : 'exterior';
      const price = pricingMatrix[categoryKey]?.[formData.vehicleType]?.[tier];
      const parsed = parsePrice(price);
      if (parsed) {
        lowTotal += parsed.low;
        highTotal += parsed.high;
        if (parsed.openEnded) hasOpenEnded = true;
        hasItems = true;
      }
    });

    return { lowTotal, highTotal, hasOpenEnded, hasItems };
  };

  const formatTotal = () => {
    const { lowTotal, highTotal, hasOpenEnded, hasItems } = calculateTotal();
    if (!hasItems) return null;
    const fmt = (n) => `$${n.toLocaleString()}`;
    if (hasOpenEnded) return `${fmt(lowTotal)}+`;
    if (lowTotal === highTotal) return fmt(lowTotal);
    return `${fmt(lowTotal)} – ${fmt(highTotal)}`;
  };

  const canProceed = () => {
    switch (currentSection) {
      case 'info':
        return formData.firstName && formData.lastName && formData.phone;
      case 'vehicle': {
        const baseValid = formData.vehicleYear && formData.vehicleMake && formData.vehicleModel && formData.vehicleType;
        if (formData.vehicleType === 'Other') {
          return baseValid && formData.customVehicleType.trim();
        }
        return baseValid;
      }
      case 'scheduling':
        return getFilledSlots().length >= 1;
      case 'services':
        return getSelectedServicesCount() > 0;
      default:
        return true;
    }
  };

  const getSlotStatus = (slotIndex, time) => {
    const currentSlot = formData.preferredSlots[slotIndex];
    if (!currentSlot.date) return { disabled: false, reason: '' };

    // Already selected in another slot in this same form
    const dup = formData.preferredSlots.some((s, i) =>
      i !== slotIndex && s.date === currentSlot.date && s.time === time
    );
    if (dup) return { disabled: true, reason: 'Already selected as another option' };

    // Backend-blocked
    const avail = availability[currentSlot.date];
    if (avail) {
      if (avail.hard.includes(time)) return { disabled: true, reason: 'Time Unavailable' };
      if (avail.soft.includes(time)) return { disabled: true, reason: 'Time Unavailable' };
    }
    return { disabled: false, reason: '' };
  };

  const isTimeDisabled = (slotIndex, time) => getSlotStatus(slotIndex, time).disabled;

  useEffect(() => {
    window.scrollTo(0, 0);
    // Pre-fetch availability for any dates already entered
    if (currentSection === 'scheduling') {
      formData.preferredSlots.forEach(s => {
        if (s.date && !availability[s.date]) {
          fetchAvailability(s.date);
        }
      });
    }
  }, [currentSection]); // eslint-disable-line react-hooks/exhaustive-deps

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Booking Request Received!</h2>
          <p className="text-zinc-400 mb-6">Thank you for choosing Mad Max Wax. We'll review your request and contact you within 24 hours with a custom quote and confirmation.</p>
          <Button onClick={() => navigate('/')} className="bg-white text-black hover:bg-zinc-200">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center border border-zinc-700">
                <Sparkles className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Mad Max Wax</h1>
                <p className="text-xs text-zinc-400">Book Your Detail</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const isActive = section.id === currentSection;
              const isCompleted = sections.findIndex(s => s.id === currentSection) > index;

              return (
                <div key={section.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        isActive
                          ? 'bg-white text-black'
                          : isCompleted
                          ? 'bg-green-500/20 text-green-500 border-2 border-green-500'
                          : 'bg-zinc-800 text-zinc-500 border-2 border-zinc-700'
                      }`}
                    >
                      {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${
                      isActive ? 'text-white' : 'text-zinc-500'
                    }`}>
                      {section.label}
                    </span>
                  </div>
                  {index < sections.length - 1 && (
                    <div className={`w-12 sm:w-20 h-0.5 mx-2 mb-8 ${
                      isCompleted ? 'bg-green-500' : 'bg-zinc-800'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Personal Information */}
          {currentSection === 'info' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Personal Information</h2>
                <p className="text-zinc-400">Let's start with your contact details.</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-zinc-300 mb-2 block">First Name *</Label>
                    <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="John" />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-zinc-300 mb-2 block">Last Name *</Label>
                    <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Doe" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-zinc-300 mb-2 block">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="(614) 123-4567" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-zinc-300 mb-2 block">Email (Optional)</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="john@example.com" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vehicle Information */}
          {currentSection === 'vehicle' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Vehicle Details</h2>
                <p className="text-zinc-400">Tell us about your vehicle.</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vehicleYear" className="text-zinc-300 mb-2 block">Year *</Label>
                    <Select value={formData.vehicleYear} onValueChange={(value) => handleSelectChange('vehicleYear', value)}>
                      <SelectTrigger id="vehicleYear" data-testid="vehicle-year-select" className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
                        {YEARS.map((year) => (
                          <SelectItem key={year} value={year} className="focus:bg-zinc-800 focus:text-white">{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="vehicleMake" className="text-zinc-300 mb-2 block">Make *</Label>
                    <Input id="vehicleMake" name="vehicleMake" value={formData.vehicleMake} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Tesla" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vehicleModel" className="text-zinc-300 mb-2 block">Model *</Label>
                    <Input id="vehicleModel" name="vehicleModel" value={formData.vehicleModel} onChange={handleInputChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Model 3" />
                  </div>
                  <div>
                    <Label htmlFor="vehicleType" className="text-zinc-300 mb-2 block">Type *</Label>
                    <Select value={formData.vehicleType} onValueChange={(value) => handleSelectChange('vehicleType', value)}>
                      <SelectTrigger id="vehicleType" data-testid="vehicle-type-select" className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                        {VEHICLE_TYPES.map((type) => (
                          <SelectItem key={type} value={type} className="focus:bg-zinc-800 focus:text-white">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.vehicleType === 'Other' && (
                  <div>
                    <Label htmlFor="customVehicleType" className="text-zinc-300 mb-2 block">Please Specify *</Label>
                    <Input id="customVehicleType" name="customVehicleType" value={formData.customVehicleType} onChange={handleInputChange} data-testid="custom-vehicle-type-input" className="bg-zinc-800 border-zinc-700 text-white" placeholder="Tell us about your vehicle type" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scheduling */}
          {currentSection === 'scheduling' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Pick Your Preferred Times</h2>
                <p className="text-zinc-400">Select up to 2 date and time options that work for you (8:00 AM – 6:00 PM).</p>
              </div>
              <div className="space-y-4">
                {formData.preferredSlots.map((slot, index) => (
                  <div key={index} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                    <p className="text-sm font-semibold text-zinc-300 mb-4">
                      Option {index + 1} {index === 0 ? '*' : '(Optional)'}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`date-${index}`} className="text-zinc-300 mb-2 block text-sm">Date</Label>
                        <Input id={`date-${index}`} type="date" value={slot.date} min={new Date().toISOString().split('T')[0]} onChange={(e) => handleSlotChange(index, 'date', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
                      </div>
                      <div>
                        <Label htmlFor={`time-${index}`} className="text-zinc-300 mb-2 block text-sm">Time</Label>
                        <Select value={slot.time} onValueChange={(value) => handleSlotChange(index, 'time', value)}>
                          <SelectTrigger id={`time-${index}`} data-testid={`time-slot-${index}`} className="bg-zinc-800 border-zinc-700 text-white" disabled={!slot.date}>
                            <SelectValue placeholder={slot.date ? "Select time" : "Pick a date first"} />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                            {TIME_SLOTS.map((ts) => {
                              const { disabled, reason } = getSlotStatus(index, ts.value);
                              return (
                                <SelectItem
                                  key={ts.value}
                                  value={ts.value}
                                  disabled={disabled}
                                  className="focus:bg-zinc-800 focus:text-white data-[disabled]:opacity-40 data-[disabled]:bg-zinc-950 data-[disabled]:line-through"
                                  title={disabled ? reason : ''}
                                >
                                  <span className="flex items-center justify-between w-full gap-3">
                                    <span>{ts.label}</span>
                                    {disabled && (
                                      <span className="text-[10px] uppercase tracking-wider text-red-400/70">
                                        {reason || 'Unavailable'}
                                      </span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {slot.date && availability[slot.date] && (availability[slot.date].hard.length > 0 || availability[slot.date].soft.length > 0) && (
                          <p className="text-xs text-zinc-500 mt-1.5">
                            Some times on this date are already taken.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Services Selection */}
          {currentSection === 'services' && (
            <div className="space-y-10">
              <div>
                <h2 className="text-3xl font-bold mb-2">Select Your Services</h2>
                <p className="text-zinc-400">Choose your main service tier, add-ons, and tell us about anything else you need.</p>
                {formData.vehicleType && (
                  <p className="text-sm text-zinc-500 mt-2">
                    Pricing shown for: <span className="text-white font-semibold">{formData.vehicleType === 'Other' ? formData.customVehicleType : formData.vehicleType}</span>
                  </p>
                )}
              </div>

              {/* Tier-based Main Services */}
              {tierServices.map((category, catIdx) => {
                const Icon = category.icon;
                const selectedTier = formData.tierSelections[category.category];
                const categoryPricing = pricingMatrix[category.key]?.[formData.vehicleType];

                // If no pricing available for this vehicle type, show notice
                if (categoryPricing === null) {
                  return (
                    <div key={catIdx} className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center border border-zinc-800">
                          <Icon className="w-5 h-5 text-zinc-300" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">{category.category}</h3>
                          <p className="text-sm text-zinc-400">{category.description}</p>
                        </div>
                      </div>
                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 text-center">
                        <p className="text-zinc-400 text-sm">
                          {category.category} is not standard for your selected vehicle type. Please mention any specific request in the notes below and we'll provide a custom quote.
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={catIdx} className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center border border-zinc-800">
                        <Icon className="w-5 h-5 text-zinc-300" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{category.category}</h3>
                        <p className="text-sm text-zinc-400">{category.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {category.tiers.map((tier) => {
                        const isSelected = selectedTier === tier.tier;
                        const price = categoryPricing ? categoryPricing[tier.tier] : 'Custom Quote';
                        return (
                          <button
                            key={tier.tier}
                            type="button"
                            onClick={() => handleTierSelect(category.category, tier.tier)}
                            data-testid={`tier-${category.category.toLowerCase().replace(/\s+/g, '-')}-${tier.tier}`}
                            className={`relative text-left p-6 rounded-lg border-2 transition-all ${
                              isSelected
                                ? 'bg-green-500/10 border-green-500 ring-2 ring-green-500/30'
                                : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'
                            }`}
                          >
                            {tier.featured && !isSelected && (
                              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-white text-black px-2 py-0.5 rounded-full font-bold">
                                Best
                              </span>
                            )}
                            {isSelected && (
                              <div className="absolute top-3 right-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div className="flex items-center justify-between mb-2">
                              <h4 className={`text-xl font-bold ${isSelected ? 'text-green-400' : 'text-white'}`}>
                                {tier.name}
                              </h4>
                            </div>
                            <p className={`text-2xl font-bold mb-3 ${isSelected ? 'text-green-300' : 'text-white'}`}>
                              {price}
                            </p>
                            <p className={`text-sm leading-relaxed ${isSelected ? 'text-zinc-200' : 'text-zinc-400'}`}>
                              {tier.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                    {categoryPricing?.note && (
                      <p className="text-xs text-zinc-500 italic pl-2">* {categoryPricing.note}</p>
                    )}
                  </div>
                );
              })}

              {/* Add-On Services */}
              <div className="space-y-4 pt-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center border border-zinc-800">
                    <Plus className="w-5 h-5 text-zinc-300" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Add-Ons</h3>
                    <p className="text-sm text-zinc-400">Enhance your detail with these extras</p>
                  </div>
                </div>

                <div className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-4">
                  <p className="text-sm text-zinc-300 text-center">
                    Add-ons are quoted differently per vehicle. Quotes provided upon selection.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {addOnServices.map((service, index) => {
                    const isSelected = formData.selectedAddOns.includes(service.name);
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleAddOnToggle(service.name)}
                        data-testid={`addon-${index}`}
                        className={`text-left p-4 rounded-lg border-2 transition-all relative ${
                          isSelected
                            ? 'bg-green-500/10 border-green-500 ring-2 ring-green-500/30'
                            : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className={`font-semibold pr-8 ${isSelected ? 'text-green-400' : 'text-white'}`}>
                            {service.name}
                          </h4>
                          {isSelected && (
                            <div className="absolute top-3 right-3 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed ${isSelected ? 'text-zinc-200' : 'text-zinc-400'}`}>
                          {service.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Other Service */}
              <div className="space-y-3 pt-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Don't See What You Need?</h3>
                  <p className="text-sm text-zinc-400">Tell us about a service you're looking for that isn't listed above.</p>
                </div>
                <Textarea
                  id="otherService"
                  name="otherService"
                  value={formData.otherService}
                  onChange={handleInputChange}
                  data-testid="other-service-input"
                  className="bg-zinc-900 border-zinc-800 text-white min-h-[80px]"
                  placeholder="e.g., a specialty wrap treatment, or pet hair removal from the cabin."
                />
              </div>
            </div>
          )}

          {/* Summary */}
          {currentSection === 'summary' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Review Your Booking</h2>
                <p className="text-zinc-400">Please confirm all details before submitting.</p>
              </div>

              <div className="space-y-4">
                {/* Personal Info Summary */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Contact Information</span>
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-zinc-400">Name:</span> <span className="text-white">{formData.firstName} {formData.lastName}</span></p>
                    <p><span className="text-zinc-400">Phone:</span> <span className="text-white">{formData.phone}</span></p>
                    {formData.email && <p><span className="text-zinc-400">Email:</span> <span className="text-white">{formData.email}</span></p>}
                  </div>
                </div>

                {/* Vehicle Info Summary */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <Car className="w-5 h-5" />
                    <span>Vehicle Details</span>
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white font-medium">{formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}</p>
                    <p><span className="text-zinc-400">Type:</span> <span className="text-white">{formData.vehicleType === 'Other' ? formData.customVehicleType : formData.vehicleType}</span></p>
                  </div>
                </div>

                {/* Schedule Summary */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <Calendar className="w-5 h-5" />
                    <span>Preferred Time Slots</span>
                  </h3>
                  <div className="space-y-2">
                    {getFilledSlots().map((slot, index) => {
                      const timeLabel = TIME_SLOTS.find(t => t.value === slot.time)?.label || slot.time;
                      return (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <Clock className="w-4 h-4 text-zinc-400" />
                          <span className="text-white">{new Date(slot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at {timeLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Services Summary */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <Sparkles className="w-5 h-5" />
                    <span>Selected Services ({getSelectedServicesCount()})</span>
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(formData.tierSelections).map(([category, tier]) => {
                      const categoryKey = category === 'Interior Detail' ? 'interior' : 'exterior';
                      const price = pricingMatrix[categoryKey]?.[formData.vehicleType]?.[tier];
                      return (
                        <div key={category} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-white text-sm">
                              {category} — <span className="text-green-400 font-semibold uppercase">{tier}</span>
                            </span>
                          </div>
                          {price && (
                            <span className="text-white font-bold text-sm">{price}</span>
                          )}
                        </div>
                      );
                    })}
                    {formData.selectedAddOns.map((service, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-white text-sm">{service}</span>
                        </div>
                        <span className="text-zinc-400 text-xs italic">Quote on request</span>
                      </div>
                    ))}
                    {formData.otherService.trim() && (
                      <div className="flex items-start space-x-2 pt-2 mt-2 border-t border-zinc-800">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-zinc-400 mb-1">Custom Service Request:</p>
                          <p className="text-white text-sm">{formData.otherService}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Estimated Total */}
                  {formatTotal() && (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-400">Estimated Total</p>
                          <p className="text-xs text-zinc-500">Tier services only — add-ons quoted separately</p>
                        </div>
                        <p className="text-2xl font-bold text-white">{formatTotal()}</p>
                      </div>
                      {(formData.selectedAddOns.length > 0 || formData.otherService.trim()) && (
                        <p className="text-xs text-zinc-500 mt-2 italic">+ Add-ons and custom requests will be quoted separately.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-6">
                <p className="text-zinc-300 text-sm text-center">
                  By submitting this request, you'll receive a custom quote within 24 hours. We'll confirm your preferred time slot based on availability.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-8 border-t border-zinc-800">
            <Button
              onClick={() => {
                const currentIndex = sections.findIndex(s => s.id === currentSection);
                if (currentIndex > 0) {
                  setCurrentSection(sections[currentIndex - 1].id);
                }
              }}
              disabled={currentSection === 'info'}
              variant="outline"
              className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentSection === 'summary' ? (
              <div className="flex flex-col items-end space-y-2">
                {submitError && (
                  <p className="text-red-400 text-sm" data-testid="submit-error">{submitError}</p>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="submit-quote-btn"
                  className="bg-white text-black hover:bg-zinc-200 font-semibold disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit Quote Request'}
                  {!submitting && <CheckCircle2 className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => {
                  const currentIndex = sections.findIndex(s => s.id === currentSection);
                  if (currentIndex < sections.length - 1) {
                    setCurrentSection(sections[currentIndex + 1].id);
                  }
                }}
                disabled={!canProceed()}
                data-testid="continue-btn"
                className="bg-white text-black hover:bg-zinc-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
